/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { INVITE_COMMITTEE_MEMBERS } from "../graphql";
import { VApplication } from "../../../application";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IBulkCommitteeInviteMethods extends IPluginMethodMap {
  sendBulkCommitteeInvites(args: any): Promise<any>;
}

export class BulkCommitteeInviteService implements IAgentPlugin {
  readonly methods: IBulkCommitteeInviteMethods;

  constructor() {
    this.methods = {
      sendBulkCommitteeInvites: this.sendBulkCommitteeInvites.bind(this),
    };
  }

  async sendBulkCommitteeInvites(args: any) {
    const inviteConfig = args?.inviteConfig;
    const configError = this.validateInviteConfig(inviteConfig);
    if (configError) {
      throw new Error(configError);
    }

    const members = this.normalizeMembers(args?.validData);
    const membersError = this.validateMembers(members);
    if (membersError) {
      throw new Error(membersError);
    }

    const input = this.buildInviteInput(inviteConfig, members);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const res = await client.mutate({
          mutation: INVITE_COMMITTEE_MEMBERS,
          variables: {
            input,
          },
          fetchPolicy: "no-cache",
        });

        return {
          message:
            res?.data?.inviteCommitteeMembers?.message ||
            "Committee invites sent successfully",
        };
      }
      throw new Error("Unable to connect to the server");
    } catch (err: any) {
      const displayMessage =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (displayMessage?.extensions?.display_message) {
        throw Error(displayMessage.extensions.display_message);
      }
      throw err;
    }
  }

  private normalizeMembers(validData: any[] = []) {
    return validData
      .map((row: any) => ({
        email: row?.email?.trim?.()?.toLowerCase?.() || row?.email,
        firstName: row?.firstName?.trim?.() || row?.firstName,
        lastName: row?.lastName?.trim?.() || row?.lastName,
      }))
      .filter((row: any) => row?.email);
  }

  private normalizeInviteType(value: any) {
    const normalized = String(value ?? "")
      .trim()
      .toLowerCase();
    switch (normalized) {
      case "open":
        return "Open";
      case "closed":
        return "Closed";
      case "discount":
        return "Discount";
      default:
        return value;
    }
  }

  private normalizePartition(value: any) {
    const normalized = String(value ?? "")
      .trim()
      .toLowerCase();
    switch (normalized) {
      case "default":
        return "Default";
      case "sectionspecific":
      case "section specific":
        return "SectionSpecific";
      case "specialreserve":
      case "special reserve":
        return "SpecialReserve";
      default:
        return value;
    }
  }

  private buildEmailEntries(members: any[]) {
    return members.map((member: any) => {
      const entry: { email: string; firstName?: string; lastName?: string } = {
        email: member.email,
      };

      if (member.firstName) {
        entry.firstName = member.firstName;
      }
      if (member.lastName) {
        entry.lastName = member.lastName;
      }

      return entry;
    });
  }

  private buildInviteInput(inviteConfig: any, members: any[]) {
    const inviteType = this.normalizeInviteType(inviteConfig?.inviteType);
    const partition = this.normalizePartition(inviteConfig?.partition);
    const hasOpenRegEmail = members.some(
      (member: any) => member?.email === "openreg@gem.com"
    );
    const resolvedInviteType =
      hasOpenRegEmail && inviteType !== "Open" ? "Open" : inviteType ?? "Closed";

    return {
      emails: this.buildEmailEntries(members),
      presentation: inviteConfig?.presentation,
      partition,
      inviteType: resolvedInviteType,
      maxInvites: parseInt(inviteConfig?.maxInvites, 10) || 0,
      sectionOptType: inviteConfig?.sectionOptType,
      sections:
        partition === "SectionSpecific" && inviteConfig?.section
          ? inviteConfig.section
          : null,
      openRegEnabled: resolvedInviteType === "Open",
    };
  }

  private validateInviteConfig(config: any) {
    if (!config) {
      return "Invite configuration is required";
    }

    const partition = config?.partition;

    if (
      config?.presentation === "" ||
      config?.presentation === null ||
      config?.presentation === undefined
    ) {
      return "Presentation cannot be Empty";
    }
    if (
      partition === "" ||
      partition === null ||
      partition === undefined ||
      partition === "-"
    ) {
      return "Partition cannot be Empty";
    }
    if (
      config?.sectionOptType === "" ||
      config?.sectionOptType === null ||
      config?.sectionOptType === undefined ||
      config?.sectionOptType === "-"
    ) {
      return "Section Option Type cannot be Empty";
    }
    if (
      config?.maxInvites === "" ||
      config?.maxInvites === null ||
      config?.maxInvites === undefined
    ) {
      return "Invites Count cannot be Empty";
    }
    const count = parseInt(config?.maxInvites, 10) || 0;
    if (count < 0) {
      return "Invites Count cannot be negative";
    }
    if (partition === "Default" || partition === "SpecialReserve") {
      if (
        config?.section !== null &&
        config?.section !== undefined &&
        config?.section !== ""
      ) {
        return `Section should be null for partition type ${partition}`;
      }
    }
    if (partition === "SectionSpecific") {
      if (
        config?.section === null ||
        config?.section === "" ||
        config?.section === "-" ||
        config?.section === undefined
      ) {
        return "Section Id Should be not empty";
      }
    }
    return null;
  }

  private validateMembers(members: any[]) {
    if (!members?.length) {
      return "At least one committee email is required";
    }

    for (let index = 0; index < members.length; index += 1) {
      const member = members[index];
      const rowLabel = `Row ${index + 1}`;

      if (!member?.email) {
        return `${rowLabel}: Email is required`;
      }

      const hasFirstName = !!member?.firstName;
      const hasLastName = !!member?.lastName;

      if (hasFirstName && !hasLastName) {
        return `${rowLabel}: Last Name is required for new users`;
      }
      if (hasLastName && !hasFirstName) {
        return `${rowLabel}: First Name is required for new users`;
      }
    }

    return null;
  }
}
